import { GridApi, NumberFilterModel, RowNode } from "ag-grid-community";
import { IShopViewSettings } from "components/product-collection/manage-shop-view/types";
import {
    ALLOCATION_BAG_ID,
    CurrencyDictionary,
    Item,
    ItemFields,
    ITEMS_ID,
    Kinds,
    NUMBER_FORMAT_CURRENCY_SYMBOL,
    PoiTag,
    TARGET_BAG_ID,
} from "constants/product-collection";
import { fromJS, List, Map, Set } from "immutable";
import { formatCurrency } from "lib/globalize-util";
import { safeParseFloat, safeParseInt } from "lib/number";
import { ajax } from "lib/observable";
import { isEqual } from "lodash";
import get from "lodash/get";
import memoizeOne from "memoize-one";
import { Observable } from "rxjs";
import siteConfig from "site-config";
import {
    AllocationBag,
    AllocationBagType,
    areBagValuesEqual,
    forEachAllocationBag,
    getBagValuesByRowId,
} from "./allocation-dimension";
import { ICollectionVersionStatus } from "./collection-version-status";
import { amphibiousGetIn } from "./objUtils";
import {
    getAllSizesFromItems,
    getVersionStatusForPoi,
    getViewSettingFields,
    getViewSettingValues,
} from "./product-collection";

const poiedItemsRegex = new RegExp(`^${ITEMS_ID}-(([a-z]|[0-9])*)\$`);
const poiedBagValueRegex = new RegExp(`^${ALLOCATION_BAG_ID}-(([a-z]|[0-9])*)\$`);
// Collection Helper Methods
interface IAdjustDestinationIndexParams {
    source: number; // The index a row starts at
    proposedDestination: number; // The potential destination index for the row
    totalLength: number; // The total length of the collection
}

export interface IShopViewFields {
    brandName: string;
    title: string;
    subTitle: string;
    color: string;
    retailPrice?: string;
    costPrice?: string;
}

export const derivePOTableFromItem = (item: Map<string, any>, deliveriesValues: string[], doorValues: string[]) => {
    const bulks = amphibiousGetIn(item, Item.BULKS);
    const defaultPOTable = fromJS({ r: [] });
    let newR = List();
    const deliveriesSet = Set(deliveriesValues);
    const doorsSet = Set(doorValues);
    bulks?.get("r")?.forEach((row) => {
        const column = row.get("c") as List<string>;
        const deliveryOrDoor = column.get(0);
        const doorOrQuantity = column.get(1);
        const newColumn = column.set(column.size - 1, "1");
        if (deliveriesValues.length) {
            newR =
                deliveriesSet.has(deliveryOrDoor) && doorsSet.has(doorOrQuantity)
                    ? newR.push(Map([["c", newColumn]]))
                    : newR;
        } else {
            newR = doorsSet.has(deliveryOrDoor) ? newR.push(Map([["c", newColumn]])) : newR;
        }
    });
    return defaultPOTable.set("r", newR);
};

/**
 * This method is used to adjust the destination of a given destination index given a start index and list length.
 *
 * - We need this function because in Operational Transformation, a "move" operation is a "remove", then an "insert".
 * - The indices change between removal and insertion, so we must adjust our destination index accordingly.
 */
export const adjustDestinationIndex = ({
    source,
    proposedDestination,
    totalLength,
}: IAdjustDestinationIndexParams): number => {
    let destination: number;
    if (proposedDestination === -1) {
        destination = totalLength;
    } else if (proposedDestination > source) {
        destination = proposedDestination + 1;
    } else {
        destination = proposedDestination;
    }
    // if either destination or totalLength are undefined, then Math.min() returns NaN
    // that's why we fall back to the proposedDestination
    return Math.min(destination, totalLength) || proposedDestination;
};

export interface IGridData {
    data: List<any>;
    aggData: Map<string, Map<string, any>>;
    groupHeaders: string[];
}

export const NODE = "_node";

export const { getViewDataFromApi, getDataFromApi } = (function () {
    const getViewDataFromApi = (api: GridApi): IGridData => {
        let data = List([]);
        let aggData = Map<string, Map<string, any>>();
        const delimiter = " → ";
        const undefValue = "N/A";
        const groupHeaders = [];
        const rowSet = [];
        const rowSetForComparison = [];

        if (api) {
            // @ts-ignore columnModel is private
            const allDisplayedColumns = api.columnModel?.getAllDisplayedColumns();
            // @ts-ignore columnModel is private
            const columnApi = api.columnModel?.columnApi;
            // `data` property is a Proxy object.
            // Don't use destructuring assignment syntax in `data` because it will unpack only the proxy's original/target object properties and not intercepted properties defined in /helpers/allocation-dimension.ts:384
            // `aggData` is a plain object, so we can use the destructuring assignment syntax.
            api.forEachNodeAfterFilterAndSort(({ group, level, aggData, data, key, field, parent }: RowNode) => {
                rowSetForComparison.push({ group, level, key, field });
                rowSet.push({
                    group,
                    level,
                    aggData: aggData ? { ...aggData } : aggData,
                    data,
                    key,
                    field,
                    columnApi,
                    parent,
                });
            });

            rowSet.forEach((row: Partial<RowNode>) => {
                if (row.group && row.level === 0) {
                    groupHeaders.push(row.key);
                }
                if (row.aggData) {
                    const rowData = { ...row.aggData };
                    Object.keys(rowData).forEach((field) => {
                        const column = allDisplayedColumns?.find((col) => col.colDef.colId === field);
                        if (column && column.colDef.pivotValueColumn) {
                            const targetField = column.colDef.pivotValueColumn.colId;
                            if (rowData[field] || !rowData[targetField]) {
                                rowData[targetField] = rowData[field];
                            }
                            delete rowData[field];
                        }
                    });

                    rowData[NODE] = row;

                    const path = [String(row.key || undefValue)];
                    let parent = row.parent;
                    while (parent && parent.level > -1) {
                        path.unshift(parent.key || undefValue);
                        parent = parent.parent;
                    }

                    const map = aggData.get(row.field) || Map<string, any>();
                    aggData = aggData.set(row.field, map.set(path.join(delimiter).toLowerCase(), rowData));
                } else if (row.data) {
                    data = data.push({
                        ...row.data,
                        // Materialise proxy metadata so Totals worker can use
                        ...(row.data.proxyIndex !== undefined
                            ? {
                                  proxyIndex: row.data.proxyIndex,
                                  breadcrumbs: row.data.breadcrumbs,
                                  quantity: row.data.quantity,
                                  positionValues: row.data.positionValues,
                              }
                            : {}),
                    });
                }
            });
        }

        return {
            data,
            aggData,
            groupHeaders,
        };
    };

    const getDataFromApi = (api: GridApi): IGridData => {
        let data = List([]);
        let aggData = Map<string, Map<string, any>>();
        const delimiter = " → ";
        const undefValue = "N/A";
        const groupHeaders = [];
        const rowSet = [];

        if (api) {
            // @ts-ignore columnModel is private
            const allDisplayedColumns = api.columnModel?.getAllDisplayedColumns();
            // @ts-ignore columnModel is private
            const columnApi = api.columnModel?.columnApi;
            // `data` property is a Proxy object.
            // Don't use destructuring assignment syntax in `data` because it will unpack only the proxy's original/target object properties and not intercepted properties defined in /helpers/allocation-dimension.ts:384
            // `aggData` is a plain object, so we can use the destructuring assignment syntax.
            api.forEachNode(({ group, level, aggData, data, key, field, parent }: RowNode) =>
                rowSet.push({
                    group,
                    level,
                    aggData: aggData ? { ...aggData } : aggData,
                    data,
                    key,
                    field,
                    columnApi,
                    parent,
                })
            );

            rowSet.forEach((row: Partial<RowNode>) => {
                if (row.group && row.level === 0) {
                    groupHeaders.push(row.key);
                }
                if (row.aggData) {
                    const rowData = { ...row.aggData };
                    Object.keys(rowData).forEach((field) => {
                        const column = allDisplayedColumns?.find((col) => col.colDef.colId === field);
                        if (column && column.colDef.pivotValueColumn) {
                            const targetField = column.colDef.pivotValueColumn.colId;
                            if (rowData[field] || !rowData[targetField]) {
                                rowData[targetField] = rowData[field];
                            }
                            delete rowData[field];
                        }
                    });

                    rowData[NODE] = row;

                    const path = [String(row.key || undefValue)];
                    let parent = row.parent;
                    while (parent && parent.level > -1) {
                        path.unshift(parent.key || undefValue);
                        parent = parent.parent;
                    }

                    const map = aggData.get(row.field) || Map<string, any>();
                    aggData = aggData.set(row.field, map.set(path.join(delimiter).toLowerCase(), rowData));
                } else if (row.data) {
                    data = data.push({
                        ...row.data,
                        // Materialise proxy metadata so Totals worker can use
                        ...(row.data.proxyIndex !== undefined
                            ? {
                                  proxyIndex: row.data.proxyIndex,
                                  breadcrumbs: row.data.breadcrumbs,
                                  quantity: row.data.quantity,
                                  positionValues: row.data.positionValues,
                              }
                            : {}),
                    });
                }
            });
        }

        return {
            data,
            aggData,
            groupHeaders,
        };
    };
    return { getViewDataFromApi, getDataFromApi };
})();

export const getFieldValue = (data: object, key: string, defaultValue: any = null) => {
    const fieldValue = get(data, ["fields", key, "string_value"], "");
    if (!fieldValue) {
        return defaultValue;
    }

    // Fe_lookup_type values store the value as "<ID>; <Description>"
    // Question: Do we need a setting like field_value_delimiter or the delimiter is always ";"?
    if (fieldValue.includes(";")) {
        return fieldValue.split(";")[0].trim();
    }

    return fieldValue;
};
export type AssortmentItem = Map<string, any>;
export type ItemList = List<AssortmentItem>;

export const areLegacyAllocationsEmpty = (cols: List<any>) =>
    !cols.size || cols.every((curr) => curr.get("c").last() >>> 0 === 0);

// Compare if size units for itemA equal itemB
export const legacyAreSizesAllocationsEqual = (
    itemA: AssortmentItem,
    itemB: AssortmentItem,
    bag = Item.ALLOCATIONS[0]
): boolean => {
    // Let itemA be the reference item.

    const allocationPath = [bag, "r"];
    const [allocationsA, allocationsB]: List<any>[] = [itemA?.getIn(allocationPath), itemB?.getIn(allocationPath)];
    if (!allocationsA || !allocationsB) {
        return (
            (!allocationsA && !allocationsB) ||
            (!allocationsA && areLegacyAllocationsEmpty(allocationsB)) ||
            (!allocationsB && areLegacyAllocationsEmpty(allocationsA))
        );
    }

    const sizeASum = {};
    const sumA = allocationsA.reduce((acc, curr) => {
        const allocationEntryAsArray = curr.get("c");
        const value = safeParseFloat(allocationEntryAsArray?.last() ?? 0, 0);
        // size is always stored as second last value
        const size = allocationEntryAsArray?.get(allocationEntryAsArray.size - 2);
        if (size) {
            sizeASum[size] = (sizeASum[size] ?? 0) + value;
            return acc + value;
        }

        return acc;
    }, 0);

    const sizeBSum = {};
    const sumB = allocationsB.reduce((acc, curr) => {
        const allocationEntryAsArray = curr.get("c");
        const value = safeParseFloat(allocationEntryAsArray?.last() ?? 0, 0);

        // size is always stored as second last value
        const size = allocationEntryAsArray?.get(allocationEntryAsArray.size - 2);
        if (size) {
            sizeBSum[size] = (sizeBSum[size] ?? 0) + value;
            return acc + value;
        }

        return acc;
    }, 0);

    return sumA === sumB && Object.keys(sizeASum).every((size) => sizeASum[size] === sizeBSum[size]);
};

export const getLegacyUnequalAllocationItems = (itemsA: ItemList, itemsB: ItemList) => {
    // Let itemsA be the reference item list.
    if (!itemsA || !itemsB) {
        return List();
    }

    const allItemsMap = itemsA.reduce(
        (acc, curr) => acc.set(curr.getIn(Item.FIELDS.ID), curr),
        Map<string, AssortmentItem>()
    );
    return itemsB.reduce((acc, curr) => {
        const currId = curr.getIn(Item.FIELDS.ID);
        const item = allItemsMap.get(currId);

        if (!item || !legacyAreSizesAllocationsEqual(curr, item)) {
            return acc.push(curr);
        }

        return acc;
    }, List<AssortmentItem>());
};

/**
 * Loop through each item to find mismatch quantities between sizes
 * @param items
 * @param allocationBagA
 * @param allocationBagB
 * @returns
 */
export const getUnequalAllocationItems = (
    items: ItemList = List([]),
    allocationBagA: AllocationBag,
    allocationBagB: AllocationBag
) => {
    // Let itemsA be the reference item list.

    return items.reduce((acc, item) => {
        const itemId = item.getIn(Item.FIELDS.ID);
        const bagValuesA = getBagValuesByRowId(AllocationBagType.allocations, allocationBagA, itemId);
        const bagValuesB = getBagValuesByRowId(AllocationBagType.allocations, allocationBagB, itemId);
        if (!areBagValuesEqual(bagValuesA, bagValuesB)) {
            return acc.push(item);
        }

        return acc;
    }, List<AssortmentItem>());
};

export const getLegacySizeBulksMap = memoizeOne((referenceItem: AssortmentItem) => {
    const allSizesSet = Set(getAllSizesFromItems(List([referenceItem])));

    const allocations = referenceItem.getIn((Item.ALLOCATIONS as string[]).concat(["r"]));
    if (allocations) {
        return allocations.reduce((acc, curr) => {
            const sizeAndValue: List<string> = curr.get("c").takeLast(2);
            const size = sizeAndValue.first();
            const value = sizeAndValue.last();
            if (allSizesSet.has(size)) {
                return acc.set(size, (acc.get(size) ?? 0) + +value);
            }
            return acc;
        }, Map<string, any>());
    }

    return allSizesSet.reduce((acc, size) => acc.set(size, 0), Map<string, any>());
});

/**
 * This function creates map of sizes to their total quantities
 * @param referenceItem Row/item
 * @param allocationBag The bag itself
 */
export const getSizeBulksMap = memoizeOne((referenceItem: AssortmentItem, allocationBag: AllocationBag) => {
    const allSizesSet = Set(getAllSizesFromItems(List([referenceItem])));

    const rowId = referenceItem.getIn(Item.FIELDS.ID);
    let reducedMap = Map<string, NumberFilterModel>();
    forEachAllocationBag(allocationBag, rowId, AllocationBagType.allocations, (dimensionValues, quantity) => {
        const size = dimensionValues[dimensionValues.length - 1];
        if (allSizesSet.has(size)) {
            reducedMap = reducedMap.set(size, reducedMap.getIn([size], 0) + safeParseInt(quantity, 0));
        }
    });

    return reducedMap;
});

const itemListToSizeBulksMap = memoizeOne((items, allocationBag?: AllocationBag) =>
    items?.reduce((acc, item) => {
        const sizeBulksMap = allocationBag ? getSizeBulksMap(item, allocationBag) : getLegacySizeBulksMap(item);
        if (!sizeBulksMap) {
            return acc;
        }
        const quantity = sizeBulksMap.reduce((sum, sizeBulk) => sum + sizeBulk, 0);
        return acc.set(
            item.getIn(Item.FIELDS.ID),
            Map([
                ["total", quantity],
                ["map", sizeBulksMap],
            ])
        );
    }, Map<string, number>())
);

type TagLabelsToItemsMapArgs = [Map<string, any>, List<Map<string, any>>];
export const snapshotsPoiTagsEquality = (newArgs: TagLabelsToItemsMapArgs, oldArgs: TagLabelsToItemsMapArgs) => {
    const [newSnapshots, newPoiTags] = newArgs;
    const [oldSnapshots, oldPoiTags] = oldArgs;

    if (oldPoiTags !== newPoiTags) {
        return false;
    }

    return !newPoiTags.some((tag) => {
        const poi = tag.get(PoiTag.POI_ID);

        const itemsKey = `${ITEMS_ID}-${poi}`;
        const snapshotItem = newSnapshots.get(itemsKey);
        const itemsCacheExpired = snapshotItem && snapshotItem !== oldSnapshots.get(itemsKey);

        const allocationBagKey = `${ALLOCATION_BAG_ID}-${poi}`;
        const allocationBagSnapshot = newSnapshots.get(allocationBagKey);
        if (allocationBagSnapshot) {
            const allocationBagCacheExpired =
                allocationBagSnapshot && allocationBagSnapshot !== oldSnapshots.get(allocationBagKey);
            return itemsCacheExpired || allocationBagCacheExpired;
        }

        return itemsCacheExpired;
    });
};

export const getTagLabelsToItemsToSizeBulksMap = memoizeOne(
    (
        snapshots: Map<string, any>,
        poiTags: List<Map<string, string>>,
        versionStatusesList: ICollectionVersionStatus[]
    ) => {
        const poisToItemsMap = snapshots.reduce((acc, value, key) => {
            const [_, poi] = poiedItemsRegex.exec(key) ?? [];
            if (poi) {
                return acc.set(poi, value);
            }
            return acc;
        }, Map<string, any>());
        const poisToAllocationBagMap = snapshots.reduce((acc, value, key) => {
            const [_, poi] = poiedBagValueRegex.exec(key) ?? [];
            if (poi) {
                return acc.set(poi, value);
            }
            return acc;
        }, Map<string, any>());

        return poiTags.reduce((labelsMap, tag) => {
            const poi = tag.get(PoiTag.POI_ID);
            const versionStatus = getVersionStatusForPoi(poi, poiTags, versionStatusesList);
            if (!versionStatus) {
                return labelsMap;
            }
            return labelsMap.set(
                versionStatus.label,
                Map([
                    [PoiTag.POI_ID, poi],
                    ["items", itemListToSizeBulksMap(poisToItemsMap.get(poi), poisToAllocationBagMap.get(poi))],
                ])
            );
        }, Map<string, any>());
    },
    snapshotsPoiTagsEquality
);

export const getBrandNameForItem = (itemDisplayProperty: List<any>, item: Record<any, any>) => {
    const fieldName = itemDisplayProperty?.find((field) => field?.get("kind") === Kinds.BRAND_NAME)?.get("field_name");
    return item && fieldName && item.getIn([ItemFields.Fields, fieldName, "string_value"]);
};

export const getRawItemDisplayProperty = memoizeOne((itemDisplayProperty?: List<Immutable.Map<string, any>>) => {
    return itemDisplayProperty ? itemDisplayProperty.toJS() : [];
});

export const getProductShopViewFields = (
    rawItemDisplayProperty,
    item: Map<string, any>,
    viewSettings?: IShopViewSettings
): IShopViewFields => {
    if (viewSettings) {
        const shopFieldSchemaHash = getViewSettingFields(viewSettings, rawItemDisplayProperty);
        const shopSettingsValue = getViewSettingValues(shopFieldSchemaHash, item.toJS(), false, []);

        return {
            brandName: shopSettingsValue[viewSettings.firstLine] ?? "-",
            title: shopSettingsValue[viewSettings.secondLine] ?? "-",
            subTitle: shopSettingsValue[viewSettings.thirdLine] ?? "-",
            color: shopSettingsValue[viewSettings.fourthLine] ?? "-",
            retailPrice: viewSettings.showRetail && (shopSettingsValue[viewSettings.retail] ?? ""),
            costPrice: viewSettings.showCost && (shopSettingsValue[viewSettings.cost] ?? ""),
        };
    }
};

export const getProductShopViewFieldsForDetail = (itemDisplayProperty, shopViewFields, item): IShopViewFields => {
    if (!item) {
        return null;
    }
    const { is_cost, is_retail, is_title, is_subtitle, is_color } = shopViewFields;
    // Get symbols
    const retailSymbol = is_retail && (is_retail.field.getIn(NUMBER_FORMAT_CURRENCY_SYMBOL) || "");
    const costSymbol = is_cost && (is_cost.field.getIn(NUMBER_FORMAT_CURRENCY_SYMBOL) || "");
    // Get values
    const retailValue = is_retail ? item.getIn(is_retail.path) : 0;
    const costValue = is_cost && (item.getIn(is_cost.path) || 0);

    // Get values with format
    const retailPrice = retailValue
        ? formatCurrency(+retailValue || 0, CurrencyDictionary[retailSymbol] || "USD")
        : "-";
    const costPrice = is_cost && (formatCurrency(+costValue || 0, CurrencyDictionary[costSymbol] || "USD") || "-");

    return {
        brandName: getBrandNameForItem(itemDisplayProperty, item),
        title: is_title ? item.getIn(is_title.path) : "-",
        subTitle: is_subtitle ? item.getIn(is_subtitle.path) : "-",
        color: is_color ? item.getIn(is_color.path) : "-",
        retailPrice,
        costPrice,
    };
};

export const getProductShopViewFieldsFallback = () => ({
    brandName: "-",
    title: "-",
    subTitle: "-",
    retailPrice: "-",
    color: "-",
});

export const getMutationBatchSizeByObjectId = (objectId: string): number => {
    let batchMaxSize = -Infinity;
    if (objectId === ITEMS_ID) {
        batchMaxSize = 25;
    } else if (objectId === ALLOCATION_BAG_ID) {
        batchMaxSize = 1000;
    } else if (objectId === TARGET_BAG_ID) {
        batchMaxSize = 1000;
    }

    return batchMaxSize;
};

export const regexForGetAllCharacterNotAlphanumeric = RegExp(/[^a-zA-Z0-9\s]+/g);

export const setItemDisplayProperty = (
    remoteAssortmentSchema: any,
    localAssortmentSchema: any,
    isServerRollup: boolean
) => {
    if (isServerRollup) {
        const newRollupSnapshot = {
            ...remoteAssortmentSchema.rollupSnapshot,
            item_display_property: { struct_value: localAssortmentSchema },
        };
        const newRollupSchema = { ...remoteAssortmentSchema, rollupSnapshot: newRollupSnapshot };
        return newRollupSchema;
    }

    const collections = remoteAssortmentSchema.collections[0];
    const currentAssortmentSchema = collections.snapshot.root.mutable_map.fields.item_display_property.struct_value;

    if (isEqual(currentAssortmentSchema, localAssortmentSchema)) {
        return remoteAssortmentSchema;
    }

    const newSnapshot = {
        ...collections.snapshot,
        root: {
            ...collections.snapshot.root,
            mutable_map: {
                ...collections.snapshot.root.mutable_map,
                fields: {
                    ...collections.snapshot.root.mutable_map.fields,
                    item_display_property: { struct_value: localAssortmentSchema },
                },
            },
        },
    };

    const newCollections = { ...collections, snapshot: newSnapshot };
    const newAssortmentSchema = { ...remoteAssortmentSchema, collections: [newCollections] };

    return newAssortmentSchema;
};

const handleError = (remoteAssortmentSchema: any) => Observable.of(remoteAssortmentSchema);

export const getLocalAssortmentSchema = (merchantName: string, remoteAssortmentSchema: any, isServerRollup: boolean) =>
    ajax({
        method: "GET",
        url: `${siteConfig.assortmentSchemaLocallyAPI}/schemas/${merchantName}.json?type=file&format=text`,
        retry: 1,
    })
        .flatMap(({ response }) => {
            const localAssortmentSchema = JSON.parse(response.content);

            if (!localAssortmentSchema) {
                throw new Error(`Local Schema not found for the ${merchantName} merchant`);
            }

            return Observable.of(setItemDisplayProperty(remoteAssortmentSchema, localAssortmentSchema, isServerRollup));
        })
        .catch((error) => {
            return handleError(remoteAssortmentSchema);
        });
